import type * as THREE from 'three';
import type { IIndicationStrategy } from '../Indications.controller';
import type { IndicationsService } from '../Indications.service';
import { Tools } from '../ToolStateStore';
import type { DistanceModel } from './Distance.model';
import { type IObjectViewModel, SceneItemAction } from '@/engine/app/interfaces';
import type { ObservabilityLoggerService } from '@/app.obresvability/loggerService/observabilityLogger.service.ts';
import type { ICommandProcessor } from '../../core/interfaces/Command';
import type { PlanningAlgorithm } from '@/engine/PlanningAlgorithm/PlanningAlgorithm';
import type { IValue } from '@/engine/DataFlow/Graph';
import type { IRemover } from '@/engine/UIPresentation/Interfaces';
import type { IIndicationAdder } from '../interfaces';
import type { CancellationController } from '@/engine/Controllers/Cancellation.controller';
import type { CallbackSubscription } from '@/engine/Utilities/CallbackManager';

export class DistanceIndicationStrategy implements IIndicationStrategy {
  private _indicationsService: IndicationsService;

  private _pointCount = 0;

  private _model?: DistanceModel;

  private _indicationViewModel?: IObjectViewModel;

  private readonly _loggerService: ObservabilityLoggerService;

  private readonly _commandProcessor: ICommandProcessor;

  private readonly _planningAlgorithm: PlanningAlgorithm;

  private readonly _remover: IRemover;

  private readonly _adder: IIndicationAdder<IValue>;

  private readonly _cancellationController: CancellationController;

  private _cancelCallback?: CallbackSubscription;

  public constructor(
    indicationsService: IndicationsService,
    planningAlgorithm: PlanningAlgorithm,
    commandProcessor: ICommandProcessor,
    adder: IIndicationAdder<IValue>,
    remover: IRemover,
    cancellationController: CancellationController
  ) {
    this._indicationsService = indicationsService;
    this._loggerService = indicationsService.loggerService;
    this._commandProcessor = commandProcessor;
    this._planningAlgorithm = planningAlgorithm;
    this._adder = adder;
    this._remover = remover;
    this._cancellationController = cancellationController;
  }

  public async onPointAddAsync(point: THREE.Vector3): Promise<void> {
    if (this._pointCount === 0) {
      await this._initializeIndication();
      this._model?.setFromPoint(point);
      this._model?.setToPoint(point);

      this._pointCount++;
      return;
    }

    if (this._pointCount === 1) {
      this._model?.setToPoint(point);
      await this._finalizeIndicationAsync();
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-misused-promises -- //TODO: [chebotarev] fix eslint no-misused-promises
  public async onPointMove(point: THREE.Vector3): Promise<void> {
    if (this._pointCount !== 1) {
      return;
    }
    this._model?.setToPoint(point);
    await this._indicationsService.actualizeIndicationAsync();
  }

  // eslint-disable-next-line @typescript-eslint/no-misused-promises -- //TODO: [chebotarev] fix eslint no-misused-promises
  public async dispose(): Promise<void> {
    if (this._pointCount === 1) {
      return this._indicationsService.rollbackIndicationAsync();
    }
    this._cancelCallback?.dispose();
  }

  private async _resetIndication(): Promise<void> {
    this._pointCount = 0;
    return this._indicationsService.rollbackIndicationAsync();
  }

  private async _initializeIndication(): Promise<void> {
    const { model, viewModel } = await this._indicationsService.initializeIndicationAsync(Tools.Distance);
    this._model = model as DistanceModel;
    this._indicationViewModel = viewModel as IObjectViewModel;

    this._cancelCallback?.dispose();
    this._cancelCallback = this._cancellationController.addCancelEvent(
      this._indicationViewModel.descriptor,
      this._resetIndication.bind(this)
    );
  }

  private async _finalizeIndicationAsync(): Promise<void> {
    if (this._indicationViewModel) {
      const viewModel = this._indicationViewModel;
      const model = this._model as IValue;

      this._cancellationController.removeCancelEvent(viewModel.descriptor);
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- //TODO: [chebotarev] fix eslint no-floating-promises
      this._indicationsService.removeIndicationAsync(this._indicationViewModel);

      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- //TODO: [chebotarev] fix eslint no-floating-promises
      this._commandProcessor.executeAnonymousCommand(
        (): Promise<undefined> => {
          // eslint-disable-next-line @typescript-eslint/no-floating-promises -- //TODO: [chebotarev] fix eslint no-floating-promises
          this._adder.addIndication(model, viewModel);
          return Promise.resolve(undefined);
        },
        (): Promise<undefined> => {
          this._remover.removeByDescriptor(viewModel.descriptor);
          return Promise.resolve(undefined);
        }
      );
      this._loggerService.dispatchSceneItemAction(SceneItemAction.IndicationAdded, this._indicationViewModel);
    }
    this._pointCount = 0;
    this._indicationViewModel = undefined;
    this._model = undefined;

    await this._indicationsService.finalizeIndicationAsync();
  }
}
